/**
 * Ephemeral annotation tool state (Zustand) — the active drawing tool, the
 * default style for new shapes, and the currently-selected annotation
 * (ADR-034; feature-spec §5.2 / §8). This is **session-only** UI state — the
 * annotations themselves persist via the BFF (:mod:`./use-annotations`); the
 * tool/selection state does not.
 *
 * ``activeTool``:
 *   - ``null`` — no draw tool active (the default). The default map cursor
 *     selects an existing annotation on click; entity glyphs are unaffected
 *     (D10 entity-first precedence).
 *   - ``"line" | "circle" | "polygon" | "marker"`` — a draw tool.
 *   - ``"edit"`` — the geometry tool (REQ-UI-135): shapes expose their handles
 *     and become draggable / resizable / reshapeable. Supersedes the former
 *     ``"hand"`` move tool, which did the same translate work with no visible
 *     affordance.
 *
 * :func:`selectTool` toggles: re-selecting the active tool clears it to
 * ``null`` (the store primitive behind D14's "Escape or re-click the active
 * tool deselects"). A draw tool also **auto-deselects after completing a
 * shape** (the post-visual-pass amendment to D14's original stay-in-tool — the
 * finish handler calls :func:`clearActiveTool`), returning to the default
 * selection cursor.
 *
 * Cross-references:
 *   - ADR-034; feature-spec §5.2 (tool-state store) / §8 (interaction model).
 *   - D10 (entity-first precedence) / D14 (re-click/Escape + auto-deselect).
 */
import { create } from "zustand";

/** The annotation tools. The draw tools create shapes; the ``edit`` tool
 *  reshapes an existing one (translate / vertex-drag / vertex-delete /
 *  midpoint-insert / circle-resize) with its handles visible. Selecting an
 *  annotation for config uses the default map cursor (no tool active) — there is
 *  no dedicated Select tool.
 *
 *  ``edit`` replaced ``hand`` (2026-08-01): the hand tool already translated
 *  shapes and dragged line vertices, but exposed no handles, so reshaping only
 *  worked if the operator already knew where to grab. One tool with visible
 *  handles removes both the invisibility and the which-tool-do-I-need question;
 *  disambiguation is by what is under the cursor, not by mode. */
export type AnnotationTool = "edit" | "line" | "circle" | "polygon" | "marker";

/** The default style applied to newly-drawn annotations. */
export interface AnnotationDrawStyle {
  readonly strokeColor: string;
  readonly strokeWidthPx: number;
  readonly fillColor: string;
  readonly fillOpacity: number;
  readonly markerColor: string;
  readonly markerSizePx: number;
}

/**
 * The initial draw style — a neutral, high-contrast amber. The theme-aware
 * design-system annotation tokens + the per-shape style controls land in later
 * increments (Inc 5 style controls / Inc 7 theming; feature-spec §10).
 */
export const DEFAULT_DRAW_STYLE: AnnotationDrawStyle = {
  strokeColor: "#f5a623",
  // Default weight (ADR-034 batch 2; thickened 1px → 2px in the batch-6 cleanup —
  // 1px read as too thin on the map). The operator adjusts per shape in the
  // Entity Details panel; selection adds a +2px boost on top.
  strokeWidthPx: 2,
  fillColor: "#f5a623",
  fillOpacity: 0.2,
  markerColor: "#f5a623",
  // px radius — sized to match the detection marker (DETECTION_RADIUS_ZOOM_STOPS
  // tops out at ~7); 12 was visually too large (ADR-034 bug fix).
  markerSizePx: 6,
};

export interface AnnotationToolState {
  /** The active tool, or ``null`` for the entity-first default (D10). */
  activeTool: AnnotationTool | null;
  /** The style new annotations are drawn with. */
  drawStyle: AnnotationDrawStyle;
  /** The selected annotation's id (default-cursor selection), or ``null``. */
  selectedId: string | null;
  /** Select a tool; re-selecting the active tool clears it to ``null`` (D14). */
  selectTool: (tool: AnnotationTool) => void;
  /** Force the active tool to ``null`` (Escape / programmatic deselect). */
  clearActiveTool: () => void;
  /** Patch the default draw style. */
  setDrawStyle: (patch: Partial<AnnotationDrawStyle>) => void;
  /** Set (or clear) the selected annotation. */
  setSelectedId: (id: string | null) => void;
  /** Reset to the initial state. */
  reset: () => void;
}

export const useAnnotationToolStore = create<AnnotationToolState>((set) => ({
  activeTool: null,
  drawStyle: DEFAULT_DRAW_STYLE,
  selectedId: null,
  selectTool: (tool) =>
    set((state) => ({
      // Toggle: re-clicking the active tool deselects (D14).
      activeTool: state.activeTool === tool ? null : tool,
      // Switching tools drops any current annotation selection.
      selectedId: null,
    })),
  clearActiveTool: () => set({ activeTool: null }),
  setDrawStyle: (patch) => set((state) => ({ drawStyle: { ...state.drawStyle, ...patch } })),
  setSelectedId: (id) => set({ selectedId: id }),
  reset: () => set({ activeTool: null, drawStyle: DEFAULT_DRAW_STYLE, selectedId: null }),
}));
